// =====================================================================================
// INVRT Radar — web console — "Send by email"
//
// POST /contacts/send with channel 'email' (docs/API-CONTRACTS-C1.md §5): one message with
// a table of the contacts and a CSV attachment, to up to ten addresses, with an optional
// note. Zero-rated on the server. The recipient list is checked here against the same
// limits routes/contacts.ts enforces, so a refusal is explained before a request is made
// rather than after; the server remains the authority.
// =====================================================================================

import { useId, useState } from "react";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/input";
import { ErrorState } from "@/components/States";
import { pluralise } from "@/lib/format";
import { MAX_SEND_IDS, MAX_SEND_RECIPIENTS, parseRecipients, useSendContacts } from "./useContacts";

const MAX_NOTE_LENGTH = 500;
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface SendContactsDialogProps {
  open: boolean;
  contactIds: string[];
  onClose: () => void;
  /** Called after the server has accepted the send (the caller clears its selection). */
  onSent?: () => void;
}

/**
 * Mounted only whilst open, so every opening starts with a clean form and a fresh mutation:
 * nothing typed or failed for one send leaks into the next.
 */
export function SendContactsDialog(props: SendContactsDialogProps) {
  if (!props.open) return null;
  return <SendContactsForm {...props} />;
}

function SendContactsForm({ contactIds, onClose, onSent }: SendContactsDialogProps) {
  const toast = useToast();
  const send = useSendContacts();
  const formId = useId();
  const [recipients, setRecipients] = useState("");
  const [note, setNote] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);

  const tooMany = contactIds.length > MAX_SEND_IDS;

  function submit(): void {
    const to = parseRecipients(recipients);
    if (to.length === 0) {
      setFieldError("Enter at least one email address.");
      return;
    }
    if (to.length > MAX_SEND_RECIPIENTS) {
      setFieldError(`Up to ${MAX_SEND_RECIPIENTS} addresses at a time.`);
      return;
    }
    const malformed = to.find((address) => !EMAIL_SHAPE.test(address));
    if (malformed) {
      setFieldError(`"${malformed}" does not look like an email address.`);
      return;
    }
    if (note.length > MAX_NOTE_LENGTH) {
      setFieldError(`Keep the note to ${MAX_NOTE_LENGTH} characters.`);
      return;
    }
    setFieldError(null);

    const trimmedNote = note.trim();
    send.mutate(
      {
        contact_ids: contactIds,
        channel: "email",
        to,
        ...(trimmedNote === "" ? {} : { note: trimmedNote }),
      },
      {
        onSuccess: (response) => {
          // The server's own figure for who it wrote to, never the list we typed.
          const delivered = response.sent_to?.length ?? to.length;
          toast.push({
            tone: "success",
            title: `Sent to ${pluralise(delivered, "address", "addresses")}`,
            body: `${pluralise(response.count, "contact")} sent as a table with a CSV attached.`,
          });
          onSent?.();
          onClose();
        },
      },
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Send by email"
      description={`${pluralise(contactIds.length, "contact")} will go out as one message with a table and a CSV attachment. Sending costs nothing.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={send.isPending}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={send.isPending} disabled={tooMany}>
            Send
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Textarea
          label="Send to"
          value={recipients}
          onChange={(event) => setRecipients(event.target.value)}
          placeholder="sales@example.com, ops@example.com"
          hint={`Up to ${MAX_SEND_RECIPIENTS} addresses, separated by commas or new lines.`}
          {...(fieldError ? { error: fieldError } : {})}
          rows={3}
        />
        <Textarea
          label="Note (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          maxLength={MAX_NOTE_LENGTH}
          hint={`Included at the top of the message. ${note.length}/${MAX_NOTE_LENGTH} characters.`}
          rows={3}
        />
        {tooMany ? (
          <p className="text-sm text-danger">
            Up to {MAX_SEND_IDS} contacts can be sent in one go. Narrow the selection and try again.
          </p>
        ) : null}
        {send.isError ? <ErrorState error={send.error} /> : null}
      </form>
    </Dialog>
  );
}
